import React, { useState, useEffect, useCallback } from "react";
// IMPORTANT: do not import loadScript here if you're only loading in the custom hook
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ContactDetails from "./contactDetails";
import CardDetails from "./CardDetails";
import { useClient } from "ClientContext";
import { useGuestyPay } from "hooks/useGuestyPay";
import { useHostawayReservation } from "hooks/useHostawayReservation";
import AddOnsSelector from "./AddonsSelector";
import { useCheckout } from "context/CheckoutContext";
import { FormData } from "data/types";
import { useGuestyReservation } from "hooks/useGuestyReservation";

interface SDKProps {
  providerId: string;           // Guesty provider ID
  Quote: any;                   // Quoting / pricing info
  listingID: string;            // your listing ID
}

const PaymentHandling: React.FC<SDKProps> = ({
  providerId,
  Quote,
  listingID,
}) => {
  const {  handleFetchReservation: handleGuestyFetch, isUpdatingStripeCustomer } = useGuestyReservation();
  const { reservation:guestyReservation } = useCheckout();

  const { reservation:hostawayReservation, handleFetchReservation:fetchHostawayreservation, reservation_error, reservation_loading } =
    useHostawayReservation();

  const {
    formData,       // all user input data
    setFormData,
    step,
    setStep,
  } = useCheckout();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cardError, setCardError] = useState("");

  // Grabbing integration_TYPE from context to decide if we're using Guesty or Hostaway
  const { global_currency, integration_TYPE, referralId } = useClient();
  const defaultCurrency = global_currency[0];

  // ------------------ Validation for Step 1 (Contact) ---------------------
  const validateStep1 = () => {
    if (!formData.guest.email || formData.guest.email.trim() === "") {
      setErrorMessage("Please enter an email address.");
      return false;
    }
    if (!formData.guest.firstName || formData.guest.firstName.trim() === "") {
      setErrorMessage("Please enter your first name.");
      return false;
    }
    if (!formData.guest.lastName || formData.guest.lastName.trim() === "") {
      setErrorMessage("Please enter your last name.");
      return false;
    }
    if (!formData.policy.termsAndConditions.isAccepted) {
      setErrorMessage("Please accept the terms and conditions.");
      return false;
    }
    if (!formData.policy.privacy.isAccepted) {
      setErrorMessage("Please accept the privacy policy.");
      return false;
    }

    if (integration_TYPE === "guesty" && (!formData.guest.phone || formData.guest.phone.trim() === "")) {
      setErrorMessage("Please enter your phone number.");
      return false;
    }
    return true;
  };

  // ------------------ Step Navigation logic ---------------------
  const goToStep = (targetStep: number) => {
    // If moving beyond Step 1, ensure Step 1 is valid
    if (targetStep > 1 && !validateStep1()) {
      return;
    }
    // Clear errors when going back
    if (targetStep < 3) {
      setErrorMessage(null);
      setCardError("");
    }
    setStep(targetStep);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateStep1()) return;
      // Only show step 2 if "hostaway"
      if (integration_TYPE === "hostaway") {
        setStep(2);
      } else {
        setStep(3);
      }
      setErrorMessage(null);

    } else if (step === 2) {
      // Step 2 only relevant if integration_TYPE is "hostaway"
      setStep(3);
      setErrorMessage(null);

    } else if (step === 3) {
      // Final submission
      setErrorMessage(null);
      if (integration_TYPE === "guesty") {
        guestyHook.handleGuestyPaymentSubmission();
      } else if (integration_TYPE === "hostaway") {
        handleHostawayPaymentSubmission();
      }
    }
  };

  // ------------------ Data Handlers from child components ---------------------
  const handleContactChange = useCallback((data: FormData) => {
    setFormData(data);
  }, [setFormData]);

  const handleAddonsChange = useCallback((data: FormData) => {
    setFormData(data);
  }, [setFormData]);

  const handleCardChange = (data: any) => {
    setFormData((prevData) => ({
      ...prevData,
      card: {
        ...prevData.card,
        ...data,
      },
    }));
  };

  // ------------------ GUESTY HOOK (initialized only at step 3) ---------------------
  const guestyHookValues = useGuestyPay({
    step,                  // pass current step
    integration_TYPE,
    Quote,
    providerId,
    listingID,
    formData,
    defaultCurrency,
    handleFetchReservation: handleGuestyFetch,
  });

  // If not "guesty", or not at step 3 yet, define a no-op.
  // However, note that the custom hook itself does the step check. 
  // This is optional, but sometimes nice to have.
  const guestyHook =
    integration_TYPE === "guesty"
      ? guestyHookValues
      : {
          renderGuestyPaymentForm: () => null,
          handleGuestyPaymentSubmission: async () => {},
          errorMessage: null,
          isLoading: false,
        };

  // ------------------ Hostaway Payment Submission ---------------------
  const handleHostawayPaymentSubmission = async () => {
    const errors: string[] = [];
    const { card } = formData;

    if (!card) {
      errors.push("Credit card details are missing.");
    } else {
      if (!card.cardholderName?.trim()) errors.push("Cardholder name is missing.");
      if (!card.cardNumber?.trim()) errors.push("Card number is missing.");
      if (!card.expirationMonth?.trim()) errors.push("Expiration month is missing.");
      if (!card.expirationYear?.trim()) errors.push("Expiration year is missing.");
      if (!card.cvv?.trim()) errors.push("CVV is missing.");
    }

    if (errors.length > 0) {
      setCardError(errors.join(" "));
      return;
    }
    setCardError("");

    // Then finalize the Hostaway reservation
    fetchHostawayreservation();
  };

  // ------------------ Step 1: Contact Form ---------------------
  const renderContactForm = () => (
    <ContactDetails
      formData={formData}
      handleContactChange={handleContactChange}
    />
  );

  // ------------------ Step 2: Hostaway Upsell Form (only if hostaway) ---------------------
  const renderHostawayUpsellForm = () => (
    <AddOnsSelector
      onChange={(selectedAddOns) => {
        // handleAddonsChange({ ...formData, addOns: selectedAddOns });
      }}
    />
  );

  // ------------------ Step 3: Payment Form (Guesty or Hostaway) ---------------------
  const renderPaymentForm = () => {
    if (integration_TYPE === "guesty") {
      // Renders the <div id="guesty-tokenization-container" />
      return guestyHook.renderGuestyPaymentForm();
    }
    if (integration_TYPE === "hostaway") {
      return <CardDetails onCardChange={handleCardChange} />;
    }
    return null;
  };

  // ------------------ Booking Confirmation (if reservation complete) ---------------------
  const renderReservationDetails = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-semibold">Booking details</h3>
      <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
      <div className="flex flex-col space-y-4">
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Booking confirmation number</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {hostawayReservation?.result.id || "N/A"}
          </span>
        </div>
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Number of guests</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {hostawayReservation?.result.numberOfGuests || "N/A"}
          </span>
        </div>
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Check-in Date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {hostawayReservation?.result.checkInTime || "N/A"}
          </span>
        </div>
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Check-out Date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {hostawayReservation?.result.checkOutTime || "N/A"}
          </span>
        </div>
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Payment method</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            Credit card
          </span>
        </div>
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Payment date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {hostawayReservation?.result.reservationDate || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );

  const renderConfirmationSection = () => (
    <div>
      <h2 className="text-3xl lg:text-4xl font-semibold">Congratulations 🎉</h2>
      {renderReservationDetails()}
      <div>
        <ButtonPrimary href="/listings">Explore more stays</ButtonPrimary>
      </div>
    </div>
  );
 
  // ------------------ Final Output: If reservation is done, show confirmation, else form ---------------------
  if (!reservation_loading && hostawayReservation || guestyReservation ) {
    return renderConfirmationSection();
  }

  return (
    <div className="space-y-8">
      <form className="w-full flex flex-col sm:rounded-2xl space-y-6 sm:space-y-8 pt-2">
        
        {/* Step Navigation */}
        <nav className="flex space-x-4 text-sm text-gray-500">
          <span
            onClick={() => goToStep(1)}
            className={`cursor-pointer ${
              step === 1 ? "text-primary-500 font-semibold" : "text-gray-400"
            }`}
          >
            Contact Details
          </span>

          {integration_TYPE === "hostaway" && (
            <>
              <span>/</span>
              <span
                onClick={() => goToStep(2)}
                className={`cursor-pointer ${
                  step === 2 ? "text-primary-500 font-semibold" : "text-gray-400"
                }`}
              >
                Add-ons
              </span>
            </>
          )}
          <span>/</span>
          <span
            onClick={() => goToStep(3)}
            className={`cursor-pointer ${
              step === 3 ? "text-primary-500 font-semibold" : "text-gray-400"
            }`}
          >
            Payment
          </span>
        </nav>

        {/* Step 1: Contact Info */}
        {step === 1 && renderContactForm()}

        {/* Step 2: Hostaway Upsell */}
        {step === 2 && integration_TYPE === "hostaway" && renderHostawayUpsellForm()}

        {/* Step 3: Payment */}
        {step === 3 && renderPaymentForm()}

        {/* Error / Validation Messages */}
        {(errorMessage || reservation_error) && (
          <div style={{ color: "red" }}>
            <p>{errorMessage || reservation_error}</p>
          </div>
        )}
        {step === 3 && cardError && <p className="text-red-600">{cardError}</p>}
        {guestyHook.errorMessage && (
          <div className="text-red-600 my-2">{guestyHook.errorMessage}</div>
        )}

        {/* Next / Submit Button */}
        <ButtonPrimary
          type="button"
          onClick={handleNext}
          className="w-full"
          loading={isLoading || reservation_loading || (step === 3 && !!referralId && isUpdatingStripeCustomer)}
          disabled={isLoading || reservation_loading || (step === 3 && !!referralId && isUpdatingStripeCustomer)}
        >
          {isLoading || reservation_loading || (step === 3 && !!referralId && isUpdatingStripeCustomer)
            ? step === 3 && !!referralId && isUpdatingStripeCustomer
              ? "Creating Reservation..."
              : "Processing..."
            : step === 1
            ? "Continue"
            : step === 2
            ? "Go To Payment"
            : "Complete Booking"}
        </ButtonPrimary>
      </form>
    </div>
  );
};

export default PaymentHandling;